/**
 * SFU-1 (L3 safety net): recover from stale-chunk errors after a deploy.
 *
 * When a new build is deployed, an already-open tab may try to lazy-load a
 * chunk whose content-hashed filename no longer exists on the server, which
 * Vite surfaces as a `vite:preloadError` event. Instead of letting that bubble
 * into a white screen, we reload the page once to pick up the fresh
 * `index.html` (and its new chunk hashes).
 *
 * The reload is guarded by a `sessionStorage` flag so a genuinely-missing chunk
 * (offline, or a new build that is itself broken) cannot cause an infinite
 * reload loop: at most one automatic reload per episode. The flag is cleared a
 * few seconds after a healthy load, so a later unrelated deploy can recover
 * again.
 */
const RELOAD_GUARD_KEY = 'tt:chunk-reload';
const CLEAR_DELAY_MS = 5000;

/** App label for the chunk-error telemetry beacon (SFU-5 / AC7). */
const APP_NAME = 'core-frontend';
/** Gateway telemetry sink — increments `gateway_frontend_chunk_load_errors_total`. */
const BEACON_URL = '/api/v1/telemetry/chunk-error';

/**
 * SFU-5 (AC7 / Obs): best-effort telemetry beacon fired on every
 * `vite:preloadError` episode so Victoria Metrics can count chunk-load failures
 * (a post-deploy seamlessness regression — or an S3/static-host outage — shows up
 * as a spike). Uses `navigator.sendBeacon` so it survives the immediate reload;
 * fully guarded so a missing API / offline state never throws into the recovery
 * path (the reload must always proceed).
 */
function defaultReportChunkError(): void {
  try {
    const body = JSON.stringify({ app: APP_NAME });
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(BEACON_URL, new Blob([body], { type: 'application/json' }));
    }
  } catch {
    // telemetry is best-effort — never block or break chunk recovery
  }
}

/**
 * Registers the chunk-load recovery listeners on `window`.
 * @param reload - reload strategy (injectable for tests); defaults to a full page reload.
 * @param reportChunkError - telemetry beacon (injectable for tests); defaults to a
 *   best-effort `navigator.sendBeacon` to the gateway (SFU-5 AC7).
 * @returns a disposer that removes the listeners (used by tests; the app ignores it).
 */
export function installChunkReloadGuard(
  reload: () => void = () => window.location.reload(),
  reportChunkError: () => void = defaultReportChunkError,
): () => void {
  const onPreloadError = (event: Event) => {
    // SFU-5 AC7: count every episode (fires before the reload). Wrapped so a
    // throwing reporter can never stop the recovery reload below.
    try {
      reportChunkError();
    } catch {
      /* telemetry must never break chunk recovery */
    }
    if (sessionStorage.getItem(RELOAD_GUARD_KEY)) return; // already reloaded once — don't loop
    sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
    event.preventDefault(); // suppress Vite's default throw so we control recovery
    reload();
  };

  const onLoad = () => {
    window.setTimeout(() => sessionStorage.removeItem(RELOAD_GUARD_KEY), CLEAR_DELAY_MS);
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  window.addEventListener('load', onLoad);

  return () => {
    window.removeEventListener('vite:preloadError', onPreloadError);
    window.removeEventListener('load', onLoad);
  };
}
